import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';

const CalendarContext = createContext();

export function useCalendar() {
  return useContext(CalendarContext);
}

export function CalendarProvider({ children }) {
  const { currentUser } = useAuth();
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to safely get date from Firestore Timestamp or Date object
  const getDateFromData = (dateData) => {
    if (!dateData) return null;
    if (dateData?.toDate && typeof dateData.toDate === 'function') {
      return dateData.toDate();
    }
    if (dateData instanceof Date) {
      return dateData;
    }
    // If it's a string or other format, try to convert to Date
    try {
      return new Date(dateData);
    } catch (error) {
      console.error('Error converting date data:', dateData, error);
      return null;
    }
  };

  // Fetch unavailable dates for the current user
  const fetchUnavailableDates = useCallback(async () => {
    if (!currentUser) {
      console.log('CalendarContext: No current user, clearing unavailable dates');
      setUnavailableDates([]);
      return;
    }

    console.log('CalendarContext: Fetching unavailable dates for user:', currentUser.uid);
    setLoading(true);
    setError(null);

    try {
      // Query unavailable dates for the current user ONLY
      const datesQuery = query(
        collection(db, 'unavailableDates'),
        where('userId', '==', currentUser.uid)
      );

      const querySnapshot = await getDocs(datesQuery);
      const datesData = [];
      const datesToDelete = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Double-check that the userId matches the current user
        if (data.userId === currentUser.uid) {
          const date = getDateFromData(data.date);

          // Check if date is in the past
          if (date && date < today) {
            datesToDelete.push(doc.id);
          } else {
            datesData.push({
              id: doc.id,
              ...data
            });
          }
        } else {
          console.warn('CalendarContext: Found date with mismatched userId:', data.userId, 'expected:', currentUser.uid);
        }
      });

      // Delete past dates in background
      if (datesToDelete.length > 0) {
        console.log(`CalendarContext: Found ${datesToDelete.length} past dates to delete`);
        Promise.all(datesToDelete.map(id => deleteDoc(doc(db, 'unavailableDates', id))))
          .then(() => console.log('CalendarContext: Successfully deleted past dates'))
          .catch(err => console.error('CalendarContext: Error deleting past dates:', err));
      }

      console.log('CalendarContext: Fetched', datesData.length, 'valid unavailable dates for user:', currentUser.uid);
      setUnavailableDates(datesData);
    } catch (error) {
      console.error('CalendarContext: Error fetching unavailable dates:', error);

      let errorMessage = 'Failed to fetch unavailable dates. Please try again.';

      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check Firestore security rules.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Firestore is currently unavailable. Please try again later.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }

      setError(errorMessage);
      setUnavailableDates([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Get group member availability for a specific group
  const getGroupMemberAvailability = useCallback(async (groupId) => {
    if (!currentUser || !groupId) return {};

    try {
      console.log('CalendarContext: This function is deprecated. Use GroupAvailabilityContext instead.');
      return {};

    } catch (error) {
      console.error('CalendarContext: Error in deprecated function:', error);
      return {};
    }
  }, [currentUser]);

  // Mark a date as unavailable
  const markDateUnavailable = useCallback(async (date, reason = '') => {
    if (!currentUser) {
      throw new Error('User must be authenticated to mark dates unavailable');
    }

    try {
      console.log('CalendarContext: Marking date unavailable for user:', currentUser.uid, 'date:', date, 'reason:', reason);

      // Check if date is already marked for this user
      const existingDate = unavailableDates.find(d => {
        const dateFromData = getDateFromData(d.date);
        return dateFromData && dateFromData.toDateString() === date.toDateString();
      });

      if (existingDate) {
        throw new Error('Date is already marked as unavailable');
      }

      const newUnavailableDate = {
        userId: currentUser.uid,
        date: date,
        reason: reason.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Add to Firestore
      const docRef = await addDoc(collection(db, 'unavailableDates'), newUnavailableDate);

      // Add to local state
      const createdDate = {
        id: docRef.id,
        ...newUnavailableDate,
        date: date,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setUnavailableDates(prev => [...prev, createdDate]);

      console.log('CalendarContext: Date marked unavailable successfully for user:', currentUser.uid);
      return createdDate;
    } catch (error) {
      console.error('CalendarContext: Error marking date unavailable:', error);
      throw error;
    }
  }, [currentUser, unavailableDates]);

  // Mark a date as available (remove from unavailable list)
  const markDateAvailable = useCallback(async (dateId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to mark dates available');
    }

    try {
      console.log('CalendarContext: Marking date available for user:', currentUser.uid, 'dateId:', dateId);

      // Verify the date belongs to the current user before deleting
      const dateToDelete = unavailableDates.find(d => d.id === dateId);
      if (!dateToDelete || dateToDelete.userId !== currentUser.uid) {
        throw new Error('Cannot delete unavailable date: not found or not owned by current user');
      }

      // Delete from Firestore
      await deleteDoc(doc(db, 'unavailableDates', dateId));

      // Remove from local state
      setUnavailableDates(prev => prev.filter(d => d.id !== dateId));

      console.log('CalendarContext: Date marked available successfully for user:', currentUser.uid);
      return { success: true };
    } catch (error) {
      console.error('CalendarContext: Error marking date available:', error);
      throw error;
    }
  }, [currentUser, unavailableDates]);

  // Update reason for unavailable date
  const updateUnavailableDateReason = useCallback(async (dateId, reason) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to update unavailable dates');
    }

    try {
      console.log('CalendarContext: Updating reason for user:', currentUser.uid, 'dateId:', dateId, 'reason:', reason);

      // Verify the date belongs to the current user before updating
      const dateToUpdate = unavailableDates.find(d => d.id === dateId);
      if (!dateToUpdate || dateToUpdate.userId !== currentUser.uid) {
        throw new Error('Cannot update unavailable date: not found or not owned by current user');
      }

      // Update in Firestore
      const dateRef = doc(db, 'unavailableDates', dateId);
      await updateDoc(dateRef, {
        reason: reason.trim(),
        updatedAt: serverTimestamp()
      });

      // Update local state
      setUnavailableDates(prev => prev.map(d =>
        d.id === dateId
          ? { ...d, reason: reason.trim(), updatedAt: new Date() }
          : d
      ));

      console.log('CalendarContext: Date reason updated successfully for user:', currentUser.uid);
      return { success: true };
    } catch (error) {
      console.error('CalendarContext: Error updating date reason:', error);
      throw error;
    }
  }, [currentUser, unavailableDates]);

  // Check if a specific date is unavailable
  const isDateUnavailable = useCallback((date) => {
    if (!date || !currentUser) return false;

    return unavailableDates.some(d => {
      // Double-check that the date belongs to the current user
      if (d.userId !== currentUser.uid) return false;

      const dateFromData = getDateFromData(d.date);
      return dateFromData && dateFromData.toDateString() === date.toDateString();
    });
  }, [unavailableDates, currentUser]);

  // Get unavailable date data for a specific date
  const getUnavailableDateData = useCallback((date) => {
    if (!date || !currentUser) return null;

    return unavailableDates.find(d => {
      // Double-check that the date belongs to the current user
      if (d.userId !== currentUser.uid) return null;

      const dateFromData = getDateFromData(d.date);
      return dateFromData && dateFromData.toDateString() === date.toDateString();
    });
  }, [unavailableDates, currentUser]);

  // Get unavailable dates for a date range
  const getUnavailableDatesInRange = useCallback((startDate, endDate) => {
    if (!startDate || !endDate || !currentUser) return [];

    return unavailableDates.filter(d => {
      // Double-check that the date belongs to the current user
      if (d.userId !== currentUser.uid) return false;

      const date = getDateFromData(d.date);
      return date && date >= startDate && date <= endDate;
    });
  }, [unavailableDates, currentUser]);

  // Bulk mark dates as unavailable
  const bulkMarkDatesUnavailable = useCallback(async (dates, reason = '') => {
    if (!currentUser) {
      throw new Error('User must be authenticated to mark dates unavailable');
    }

    try {
      console.log('CalendarContext: Bulk marking dates unavailable for user:', currentUser.uid, 'dates:', dates.length);

      const results = [];

      for (const date of dates) {
        try {
          const result = await markDateUnavailable(date, reason);
          results.push({ success: true, date, result });
        } catch (error) {
          results.push({ success: false, date, error: error.message });
        }
      }

      console.log('CalendarContext: Bulk operation completed for user:', currentUser.uid, 'results:', results);
      return results;
    } catch (error) {
      console.error('CalendarContext: Error in bulk operation:', error);
      throw error;
    }
  }, [currentUser, markDateUnavailable]);

  // Refresh calendar data
  const refreshCalendar = useCallback(() => {
    console.log('CalendarContext: Refreshing calendar for user:', currentUser?.uid);
    fetchUnavailableDates();
  }, [fetchUnavailableDates, currentUser]);

  // Clear all data when user changes or logs out
  const clearCalendarData = useCallback(() => {
    console.log('CalendarContext: Clearing calendar data');
    setUnavailableDates([]);
    setLoading(false);
    setError(null);
  }, []);

  // Load unavailable dates when user changes
  useEffect(() => {
    if (currentUser) {
      console.log('CalendarContext: User changed to:', currentUser.uid, 'fetching unavailable dates');
      fetchUnavailableDates();
    } else {
      console.log('CalendarContext: No user, clearing calendar data');
      clearCalendarData();
    }
  }, [currentUser, fetchUnavailableDates, clearCalendarData]);

  const value = {
    unavailableDates,
    loading,
    error,
    markDateUnavailable,
    markDateAvailable,
    updateUnavailableDateReason,
    isDateUnavailable,
    getUnavailableDateData,
    getUnavailableDatesInRange,
    bulkMarkDatesUnavailable,
    getGroupMemberAvailability,
    refreshCalendar
  };

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
}
